<template>
  <div v-loading="loading" class="core-overview">
    <section class="core-section">
      <h3 class="section-title"><span class="title-bar" />{{ d.dealerSection.title }}</h3>
      <div class="core-goals-grid core-goals-grid--dealer">
        <article
          v-for="goal in d.dealerSection.goals"
          :key="goal.key"
          class="core-dealer-card"
        >
          <div class="core-dealer-head" :class="'core-dealer-head--' + goal.key">
            <span class="core-dealer-title">{{ goal.title }}</span>
            <span class="core-dealer-watermark" aria-hidden="true">
              <dashboard-icons :name="goal.icon" :size="56" color="rgba(255,255,255,0.25)" />
            </span>
          </div>
          <div class="core-goal-body">
            <core-goal-metrics :goal="goal" :fmt-value="fmtCoreValue" />
            <div v-if="goal.showRegions" class="core-regions">
              <div v-for="region in goal.regions" :key="region.name" class="core-region-row">
                <span class="core-region-name">{{ region.name }}</span>
                <span class="core-region-rate">达成率 {{ Number(region.rate || 0).toFixed(2) }}%</span>
              </div>
            </div>
          </div>
        </article>
      </div>
    </section>

    <section class="core-section">
      <h3 class="section-title"><span class="title-bar" />{{ d.directSection.title }}</h3>
      <div class="core-goals-grid core-goals-grid--direct">
        <article
          v-for="goal in d.directSection.goals"
          :key="goal.key"
          class="core-direct-card"
        >
          <div class="core-direct-head">
            <span class="core-direct-icon">
              <dashboard-icons :name="goal.icon" size="16" color="#1890ff" />
            </span>
            <span class="core-direct-title">{{ goal.title }}</span>
          </div>
          <div class="core-goal-body core-goal-body--direct">
            <core-goal-metrics :goal="goal" :fmt-value="fmtCoreValue" theme="direct" />
          </div>
        </article>
      </div>
    </section>

    <section class="core-section">
      <h3 class="section-title"><span class="title-bar" />{{ d.ecommerceSection.title }}</h3>
      <div class="core-goals-grid core-goals-grid--direct core-goals-grid--ecommerce">
        <article
          v-for="goal in d.ecommerceSection.goals"
          :key="goal.key"
          class="core-direct-card"
        >
          <div class="core-direct-head">
            <span class="core-direct-icon">
              <dashboard-icons :name="goal.icon" size="16" color="#1890ff" />
            </span>
            <span class="core-direct-title">{{ goal.title }}</span>
          </div>
          <div class="core-goal-body core-goal-body--direct">
            <core-goal-metrics :goal="goal" :fmt-value="fmtCoreValue" theme="direct" />
          </div>
        </article>
      </div>
    </section>
  </div>
</template>

<script>
import { ElMessage } from 'element-plus'
import DashboardIcons from './DashboardIcons.vue'
import CoreGoalMetrics from './CoreGoalMetrics.vue'
import { getDashboardCoreOverview } from '@/api/dashboard'
import { emptyCoreDashboardData } from './dashboardCoreMock'
import { formatDashboardNum, formatDashboardAmount } from './dashboardLeaseMock'

export default {
  name: 'DashboardCoreOverview',
  components: { DashboardIcons, CoreGoalMetrics },
  data() {
    return {
      loading: false,
      d: emptyCoreDashboardData(),
    }
  },
  mounted() {
    this.loadData()
  },
  methods: {
    async loadData() {
      this.loading = true
      try {
        const res = await getDashboardCoreOverview()
        if (res && res.data) {
          this.d = mergeCoreData(res.data)
        }
      } catch (e) {
        ElMessage.error(e.message || '加载核心业绩概况失败')
      } finally {
        this.loading = false
      }
    },
    fmtCoreValue(value, unit) {
      if (unit === '元') return formatDashboardAmount(value)
      return formatDashboardNum(value)
    },
  },
}

function mergeCoreData(data) {
  const empty = emptyCoreDashboardData()
  const mergeSection = (src, fallback) => ({
    title: src?.title || fallback.title,
    goals: Array.isArray(src?.goals) ? src.goals.map(g => ({
      ...g,
      regions: Array.isArray(g.regions) ? g.regions : [],
    })) : fallback.goals,
  })
  return {
    dealerSection: mergeSection(data.dealerSection, empty.dealerSection),
    directSection: mergeSection(data.directSection, empty.directSection),
    ecommerceSection: mergeSection(data.ecommerceSection, empty.ecommerceSection),
  }
}
</script>

<style scoped>
.core-overview {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;
}

.core-section {
  min-width: 0;
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  padding: 16px 18px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  box-sizing: border-box;
}

.section-title {
  display: flex;
  align-items: center;
  margin: 0 0 14px;
  font-size: 16px;
  font-weight: 600;
  color: #262626;
  line-height: 1.4;
}

.title-bar {
  display: inline-block;
  width: 4px;
  height: 16px;
  margin-right: 8px;
  background: #1890ff;
  border-radius: 1px;
  flex-shrink: 0;
}

.core-goals-grid {
  display: grid;
  gap: 12px;
  width: 100%;
}

.core-goals-grid--dealer {
  grid-template-columns: repeat(5, minmax(0, 1fr));
}

.core-goals-grid--direct {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.core-goals-grid--ecommerce {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

/* 经销业务目标卡 */
.core-dealer-card {
  display: flex;
  flex-direction: column;
  min-width: 0;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
  border: 1px solid #e8e8e8;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.core-dealer-head {
  position: relative;
  padding: 12px 14px;
  min-height: 44px;
  display: flex;
  align-items: center;
  color: #fff;
}

.core-dealer-head--sales {
  background: linear-gradient(135deg, #69b1ff, #2f6fd6);
}

.core-dealer-head--lease {
  background: linear-gradient(135deg, #5b9fff, #1890ff);
}

.core-dealer-head--buyout {
  background: linear-gradient(135deg, #7c9cff, #4a6fd6);
}

.core-dealer-head--collection {
  background: linear-gradient(135deg, #b37feb, #722ed1);
}

.core-dealer-head--recruit {
  background: linear-gradient(135deg, #9254de, #531dab);
}

.core-dealer-title {
  font-size: 15px;
  font-weight: 600;
  line-height: 1.3;
  position: relative;
  z-index: 1;
}

.core-dealer-watermark {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  line-height: 0;
  pointer-events: none;
}

.core-goal-body {
  padding: 14px 14px 12px;
  min-width: 0;
}

.core-regions {
  border-top: 1px solid #f0f0f0;
  padding-top: 12px;
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.core-region-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 8px;
  font-size: 13px;
  line-height: 1.55;
  min-height: 22px;
}

.core-region-name {
  color: #595959;
  font-weight: 400;
  min-width: 0;
}

.core-region-rate {
  color: #262626;
  font-weight: 600;
  white-space: nowrap;
  flex-shrink: 0;
}

/* 直营 / 电商业务目标卡 */
.core-direct-card {
  background: #f0f6ff;
  border: 1px solid #dce6f5;
  border-radius: 8px;
  padding: 14px 16px;
  min-width: 0;
  box-shadow: 0 1px 2px rgba(24, 144, 255, 0.06);
}

.core-direct-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  padding-bottom: 10px;
  border-bottom: 1px solid #dce6f5;
}

.core-direct-icon {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 1px 2px rgba(24, 144, 255, 0.12);
}

.core-direct-title {
  font-size: 15px;
  font-weight: 600;
  color: #262626;
}

.core-goal-body--direct {
  padding: 0;
}

@media (max-width: 1400px) {
  .core-goals-grid--dealer {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 1100px) {
  .core-goals-grid--dealer {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .core-goals-grid--direct {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .core-goals-grid--ecommerce {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 640px) {
  .core-goals-grid--dealer,
  .core-goals-grid--direct,
  .core-goals-grid--ecommerce {
    grid-template-columns: 1fr;
  }
}
</style>
